// GrowSumo/PartnerStack integration service
// This service provides methods to interact with the GrowSumo object loaded from PartnerStack

declare global {
  interface Window {
    growsumo: {
      _initialize: (key: string) => void;
      createParticipant: (data: GrowSumoParticipant) => void;
      updateParticipant: (data: Partial<GrowSumoParticipant>) => void;
      trackEvent: (eventName: string, data?: any) => void;
      identify: (userId: string, data?: any) => void;
    };
  }
}

export interface GrowSumoParticipant {
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  companyName?: string;
  referralKey?: string;
  partnerId?: string;
  campaignId?: string;
  // Add other fields as needed based on PartnerStack documentation
}

export interface GrowSumoEventData {
  email?: string;
  value?: number;
  currency?: string;
  [key: string]: any;
}

class GrowSumoService {
  private readonly isProduction = import.meta.env.PROD;
  private readonly debugMode = import.meta.env.VITE_DEBUG_MODE === 'true';

  private isGrowSumoLoaded(): boolean {
    return typeof window !== 'undefined' && window.growsumo && typeof window.growsumo.createParticipant === 'function';
  }

  private waitForGrowSumo(timeout = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isGrowSumoLoaded()) {
        resolve();
        return;
      }

      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.isGrowSumoLoaded()) {
          clearInterval(checkInterval);
          resolve();
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          reject(new Error('GrowSumo failed to load within timeout'));
        }
      }, 100);
    });
  }

  /**
   * Create a new participant in GrowSumo when a user signs up
   */
  async createParticipant(participantData: GrowSumoParticipant): Promise<void> {
    try {
      await this.waitForGrowSumo();

      if (this.debugMode || !this.isProduction) {
        console.log('🌱 Creating GrowSumo participant:', participantData);
      }

      window.growsumo.createParticipant(participantData);

      if (this.debugMode || !this.isProduction) {
        console.log('✅ GrowSumo participant created successfully');
      }
    } catch (error) {
      console.error('❌ Failed to create GrowSumo participant:', error);
      // Don't throw error to avoid breaking the signup flow
    }
  }

  /**
   * Update an existing participant's information
   */
  async updateParticipant(participantData: Partial<GrowSumoParticipant>): Promise<void> {
    try {
      await this.waitForGrowSumo();
      
      console.log('🌱 Updating GrowSumo participant:', participantData);
      window.growsumo.updateParticipant(participantData);
      
      console.log('✅ GrowSumo participant updated successfully');
    } catch (error) {
      console.error('❌ Failed to update GrowSumo participant:', error);
    }
  }

  /**
   * Track a custom event (e.g., subscription, purchase, etc.)
   */
  async trackEvent(eventName: string, eventData?: GrowSumoEventData): Promise<void> {
    try {
      await this.waitForGrowSumo();
      
      console.log('🌱 Tracking GrowSumo event:', eventName, eventData);
      window.growsumo.trackEvent(eventName, eventData);
      
      console.log('✅ GrowSumo event tracked successfully');
    } catch (error) {
      console.error('❌ Failed to track GrowSumo event:', error);
    }
  }

  /**
   * Identify a user (useful for linking existing users)
   */
  async identify(userId: string, userData?: any): Promise<void> {
    try {
      await this.waitForGrowSumo();
      
      console.log('🌱 Identifying GrowSumo user:', userId, userData);
      window.growsumo.identify(userId, userData);
      
      console.log('✅ GrowSumo user identified successfully');
    } catch (error) {
      console.error('❌ Failed to identify GrowSumo user:', error);
    }
  }

  /**
   * Track signup event specifically
   */
  async trackSignup(participantData: GrowSumoParticipant): Promise<void> {
    // First create the participant
    await this.createParticipant(participantData);
    
    // Then track the signup event
    await this.trackEvent('signup', {
      email: participantData.email,
      firstName: participantData.firstName,
      lastName: participantData.lastName
    });
  }
}

// Export a singleton instance
export const growSumoService = new GrowSumoService();
export default growSumoService;
